import { IS_AWS_ENV } from "constants/app.constants";
import { RigFracActivityMetric } from "constants/charts.enums";
import { RigFracActivityOnlyMetric } from "types";

import { DEFAULT_RIG_FRAC_ACTIVITY_REQUEST } from "./rigFracActivity";
import { DEFAULT_RIG_FRAC_RECENT_ACTIVITY_REQUEST } from "./rigFracRecentActivity";

// Only the fields this reads, so every caller's own selections shape satisfies
// it structurally — the two charts disagree on the `chartType` literal.
type RigFracMetricSelections = Maybe<{
  primaryMetric?: RigFracActivityMetric;
  secondaryMetric?: RigFracActivityMetric;
  metric?: RigFracActivityMetric;
  leftMetric?: RigFracActivityMetric;
  rightMetric?: RigFracActivityMetric;
}>;

const isActivityOnly = (
  metric: Maybe<RigFracActivityMetric>
): metric is RigFracActivityOnlyMetric =>
  metric === RigFracActivityMetric.RigCount ||
  metric === RigFracActivityMetric.FracCrewCount ||
  // Falls back to the default off AWS so Azure never posts a metric chart-svc rejects.
  (IS_AWS_ENV && metric === RigFracActivityMetric.ApprovedPermits);

/**
 * Resolves the chart's metrics from a stored selection.
 *
 * The single place the legacy-key precedence is expressed. `primaryMetric` /
 * `secondaryMetric` replaced the EVA-76 `leftMetric` / `rightMetric` wire names,
 * but dashboards saved before that change carry only the legacy keys, so an
 * absent new key must fall through to them before the default — otherwise a
 * saved selection silently reverts to a count on load.
 *
 * Every reader goes through here rather than reading the fields directly. The
 * controller resolves them for the POST, but the selectors read the raw entity
 * request, which is seeded straight from the saved dashboard before the
 * controller's echo lands — reading a field directly there would show the
 * default for a legacy dashboard until the first response arrived.
 */
export const resolveRigFracMetrics = (
  selections: RigFracMetricSelections
): {
  primaryMetric: RigFracActivityOnlyMetric;
  secondaryMetric: RigFracActivityMetric;
  metric: RigFracActivityOnlyMetric;
} => {
  // The bars and Recent Activity's single metric cannot be a price or None —
  // only the secondary axis can. A stored document holding one there is bad data,
  // so fall back to the default rather than send a value the server will reject.
  const storedPrimary = selections?.primaryMetric ?? selections?.leftMetric;
  const primaryMetric = isActivityOnly(storedPrimary)
    ? storedPrimary
    : DEFAULT_RIG_FRAC_ACTIVITY_REQUEST.primaryMetric;

  const storedSecondary = selections?.secondaryMetric ?? selections?.rightMetric;
  const secondaryMetric =
    storedSecondary &&
    (IS_AWS_ENV || storedSecondary !== RigFracActivityMetric.ApprovedPermits)
      ? storedSecondary
      : DEFAULT_RIG_FRAC_ACTIVITY_REQUEST.secondaryMetric;

  // Recent Activity's metric falls back to the primary for continuity when
  // switching over from Activity Over Time.
  const storedMetric =
    selections?.metric ?? selections?.primaryMetric ?? selections?.leftMetric;
  const metric = isActivityOnly(storedMetric)
    ? storedMetric
    : DEFAULT_RIG_FRAC_RECENT_ACTIVITY_REQUEST.metric;

  return { primaryMetric, secondaryMetric, metric };
};
